import { encodeAdvancedFilters, decodeAdvancedFilters } from '../Filters/filterUrl'

// Advanced filters are persisted per board in the member's `taskFilterConfig`
// preference, keyed the same way the view-type and column preferences are.
//
// Each board's entry is the same encoded string the `?filter=` URL param carries,
// not the expanded filter objects -- one compact value per board, encoded and
// decoded by the same pair the URL round-trips through, so the two representations
// cannot drift.
export const taskFilterBoardKey = (boardId) => boardId || "tasks"

const isFilter = (value) =>
    !!value && typeof value === "object" && typeof value.fieldName === "string" && typeof value.filterType === "string"

const parse = (json) => {
    try {
        const parsed = JSON.parse(json || "{}")
        return parsed && typeof parsed === "object" ? parsed : {}
    } catch (e) {
        return {}
    }
}

// Accepts a value copied straight out of an address bar (where the base64 padding
// shows as `%3D`) as well as the raw form this module writes. Base64 contains no
// `%`, so this is a no-op on our own values.
const decodeParamValue = (value) => {
    try {
        return decodeURIComponent(value)
    } catch (e) {
        return value
    }
}

// The saved filters for this board, or null when there is nothing usable.
//
// Nothing is migrated. Anything written before this change is a plain object rather
// than a string, so it fails the `typeof` check and the board falls back to its
// presets; the entry is overwritten the next time the user changes a filter. A
// string that does not decode to filter objects is treated the same way.
export const readSavedTaskFilters = (taskFilterConfigJson, boardKey) => {
    const saved = parse(taskFilterConfigJson)[boardKey]
    if (typeof saved !== "string") return null
    // "" is how a cleared list is stored; anything else is meant to carry filters.
    if (!saved) return []
    const decoded = decodeAdvancedFilters(decodeParamValue(saved))
    // decodeAdvancedFilters yields [] for junk as well as for an empty payload, so a
    // non-empty string decoding to nothing is unparsable rather than cleared.
    if (!decoded.length || !decoded.every(isFilter)) return null
    return decoded
}

// The new `taskFilterConfig` JSON, or null when this board's entry is unchanged so
// the caller can skip the mutation.
export const writeSavedTaskFilters = (taskFilterConfigJson, boardKey, filters) => {
    const config = parse(taskFilterConfigJson)
    // encodeAdvancedFilters yields null for an empty list; "" keeps the key present
    // and decodes straight back to [], so "cleared" persists as cleared.
    const next = encodeAdvancedFilters(filters || []) || ""
    if (config[boardKey] === next) return null
    return JSON.stringify({ ...config, [boardKey]: next })
}
